import { VersionRange } from '../../models/core/version-range.js';
import { DataModel, DataModelBuilder, SerializationContext, SerializedData } from '../../models/dto/dto.js';
import { fromEnumAsString, toEnumFromString } from '../../serialization/enum-utils.js';
import { MapUtils } from '../../utility/map-utils.js';

export enum PoolAttendanceGroupType {
    Classroom = 'Classroom',
    Family = 'Family',
    Adults = 'Adults',
    Student = 'Student',
    Pensioner = 'Pensioner'
}

export class PoolAttendanceGroup extends DataModel {
  static readonly TYPE_ID: string = '810819a04ea84f77868d8e9153e3fac6';
  static readonly SCHEMA_VERSION: number = 2;

  public readonly numberOfAdults: number;
  public readonly numberOfChildren: number;
  public readonly numberOfStudents: number;
  public readonly numberOfPensioners: number;

  constructor(
    public readonly groupType: PoolAttendanceGroupType,
    numberOfAdults: number | null = null,
    numberOfChildren: number | null = null,
    numberOfStudents: number | null = null,
    numberOfPensioners: number | null = null,
  ) {
    super();

    this.numberOfAdults = numberOfAdults ?? 0;
    this.numberOfChildren = numberOfChildren ?? 0;
    this.numberOfStudents = numberOfStudents ?? 0;
    this.numberOfPensioners = numberOfPensioners ?? 0;
  }

  get $typeId(): string {
    return PoolAttendanceGroup.TYPE_ID;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.exactly(PoolAttendanceGroup.SCHEMA_VERSION);
  }

  get groupSize(): number {
    switch(this.groupType) {
      case PoolAttendanceGroupType.Classroom:
      case PoolAttendanceGroupType.Family:
        return this.numberOfAdults + this.numberOfChildren;
      case PoolAttendanceGroupType.Student:
        return this.numberOfStudents;
      case PoolAttendanceGroupType.Pensioner:
        return this.numberOfPensioners;
      case PoolAttendanceGroupType.Adults:
        return this.numberOfAdults;
      default:
        throw Error(`Invalid pool attendance group type: ${this.groupType}`);
    }
  }

  static family(numberOfChildren: number): PoolAttendanceGroup {
    return new PoolAttendanceGroup(
        PoolAttendanceGroupType.Family,
        2,
        numberOfChildren,
    );
  }

  static classroom(numberOfChildren: number): PoolAttendanceGroup {
    return new PoolAttendanceGroup(
        PoolAttendanceGroupType.Classroom,
        1,
        numberOfChildren,
    );
  }

  static adults(numberOfAdults: number): PoolAttendanceGroup {
    return new PoolAttendanceGroup(
        PoolAttendanceGroupType.Adults,
        numberOfAdults,
    );
  }

  static students(numberOfStudents: number): PoolAttendanceGroup {
    return new PoolAttendanceGroup(
        PoolAttendanceGroupType.Student,
        0,
        0,
        numberOfStudents,
    );
  }

  static pensioners(numberOfPensioners: number): PoolAttendanceGroup {
    return new PoolAttendanceGroup(
        PoolAttendanceGroupType.Pensioner,
        0,
        0,
        0,
        numberOfPensioners,
    );
  }

  static fromMap(context: SerializationContext, map: SerializedData): PoolAttendanceGroup {
    return new PoolAttendanceGroup(
        toEnumFromString(MapUtils.get<string>(map, 'groupType'), PoolAttendanceGroupType),
        MapUtils.get<number>(map, 'numberOfAdults'),
        MapUtils.get<number>(map, 'numberOfChildren'),
        MapUtils.get<number>(map, 'numberOfStudents'),
        MapUtils.get<number>(map, 'numberOfPensioners'),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'groupType': fromEnumAsString(this.groupType),
      'numberOfAdults': this.numberOfAdults,
      'numberOfChildren': this.numberOfChildren,
      'numberOfStudents': this.numberOfStudents,
      'numberOfPensioners': this.numberOfPensioners,
    };
  }

  clone(): this {
    return new PoolAttendanceGroup(
        this.groupType,
        this.numberOfAdults,
        this.numberOfChildren,
        this.numberOfStudents,
        this.numberOfPensioners,
    ) as this;
  }

  isSame(other: this): boolean {
    if(other === this) {
      return true;
    }

    return other.groupType === this.groupType &&
        other.numberOfAdults === this.numberOfAdults &&
        other.numberOfChildren === this.numberOfChildren &&
        other.numberOfStudents === this.numberOfStudents &&
        other.numberOfPensioners === this.numberOfPensioners;
  }
}

export class PoolAttendanceGroupBuilder extends DataModelBuilder<PoolAttendanceGroup> {
  build(context: SerializationContext, map: SerializedData): PoolAttendanceGroup {
    return PoolAttendanceGroup.fromMap(context, map);
  }

  migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    const versionRange = MapUtils.getVersionRange(map);
    if(versionRange.contains(PoolAttendanceGroup.SCHEMA_VERSION)) {
      return map;
    }
    if(versionRange.isGreaterThan(PoolAttendanceGroup.SCHEMA_VERSION)) {
      return null; // This data comes from the FUTURE. We have no idea how to migrate it.
    }

    let version = versionRange.to;

    if(version < 2) {
      let groupType = MapUtils.getOrNull<string>(map, 'groupType_v2');
      groupType ??= MapUtils.get<string>(map, 'groupType');

      if(groupType == 'StudentOrPensioner') {
        MapUtils.set(map, 'groupType', fromEnumAsString(PoolAttendanceGroupType.Adults));
        MapUtils.set(map, 'numberOfAdults', 0);
        MapUtils.set(map, 'numberOfChildren', 0);
        MapUtils.set(map, 'numberOfStudents', 0);
        MapUtils.set(map, 'numberOfPensioners', 0);
      }
      else {
        MapUtils.set(map, 'groupType', MapUtils.getOrNull(map, 'groupType_v2') ?? MapUtils.get(map, 'groupType'));
        MapUtils.setIfNullOrUndefined(map, 'numberOfStudents', 0);
        MapUtils.setIfNullOrUndefined(map, 'numberOfPensioners', 0);
      }

      version = 2;
    }

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }
}
